var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, DialogContent, Tab, TabNav, Tabs, useToast } from '@pocketlaw/tetris';
import { useAddEsigningRecipient } from 'app/domains/esigning/hooks/useAddEsigningRecipient';
import { useEsigningRecipients } from 'app/domains/esigning/hooks/useEsigningRecipients';
import { useLastSignRequest } from 'app/domains/esigning/hooks/useLastSignRequest';
import { useUnsupportedRecipientValues } from 'app/domains/esigning/hooks/useUnsupportedRecipientValues';
import { useUpdateEsigningRecipient } from 'app/domains/esigning/hooks/useUpdateEsigningRecipient';
import { UpdateEsigningRecipientRejectionReason } from 'shared/domains/apollo/generated/types';
import { FormDialog } from 'shared/domains/forms';
import { Contacts } from './Contacts';
import { Content } from './Content';
import { Footer } from './Footer';
import { Header } from './Header';
import { useGetSchema } from './useGetSchema';
import { getInitialValues, modifyInput } from './utils';
import { useEsigning } from '../../EsigningProvider';
import { StyledTabContent } from './styled';
export function RecipientModal(props) {
    const { initialValues, onClose } = props;
    const { allRecipients } = useEsigningRecipients();
    const { refetch: refetchLastSignRequest } = useLastSignRequest();
    const { documentId, pending } = useEsigning();
    const { addToast } = useToast();
    const { isAuthenticationModeUnsupported, isNameFormatUnsupported } = useUnsupportedRecipientValues();
    const [addRecipient] = useAddEsigningRecipient();
    const [updateRecipient] = useUpdateEsigningRecipient();
    const isEdit = initialValues !== undefined;
    const initialFormValues = getInitialValues(initialValues);
    const formSchema = useGetSchema({ isEdit, allRecipients, initialEmail: initialValues === null || initialValues === void 0 ? void 0 : initialValues.email });
    const [tabId, setTabId] = useState('form');
    const handleAddRecipient = (input) => __awaiter(this, void 0, void 0, function* () {
        return addRecipient({
            variables: {
                input: Object.assign(Object.assign({}, modifyInput(input, false)), { fileSystemDocumentId: documentId }),
            },
            onSuccess: () => {
                onClose();
            },
            onError: () => {
                addToast({
                    appearance: 'danger',
                    title: t({
                        message: 'Could not add the party',
                        comment: 'Toast message for error when adding a signing party',
                    }),
                });
            },
        });
    });
    const handleUpdateRecipient = (recipientId, input) => __awaiter(this, void 0, void 0, function* () {
        return updateRecipient({
            variables: {
                recipientId,
                input: modifyInput(input, pending),
            },
            onSuccess: () => {
                onClose();
            },
            onError: (e) => {
                if (e.type === 'rejection' && e.reason === UpdateEsigningRecipientRejectionReason.AlreadySigned) {
                    refetchLastSignRequest();
                    addToast({
                        appearance: 'danger',
                        title: t({
                            comment: 'Toast title for error when updating a signing party that has signed',
                            message: 'Could not update the party',
                        }),
                        message: t({
                            comment: 'Toast message for error when updating a signing party that has signed',
                            message: 'The party has already signed the document',
                        }),
                    });
                }
                else {
                    addToast({
                        appearance: 'danger',
                        title: t({
                            message: 'Could not update the party',
                            comment: 'Toast message for error when updating a signing party',
                        }),
                    });
                }
            },
        });
    });
    const handleSubmit = (values) => __awaiter(this, void 0, void 0, function* () {
        if (isEdit) {
            return handleUpdateRecipient(initialValues.id, values);
        }
        return handleAddRecipient(values);
    });
    return (<FormDialog size="lg" validateOnMount initialTouched={{
            firstName: isEdit && isNameFormatUnsupported(initialFormValues),
            lastName: isEdit && isNameFormatUnsupported(initialFormValues),
            authenticationMode: isEdit && isAuthenticationModeUnsupported(initialFormValues),
        }} initialValues={initialFormValues} validationSchema={formSchema} onSubmit={handleSubmit} onClose={onClose}>
      <Header isEdit={isEdit} onClose={onClose}/>

      <Tabs activeTab={tabId} onSelect={setTabId}>
        <Box mx="2xl" flexDirection="column">
          <TabNav>
            <Tab id="form">
              <Trans comment="Label for e-signing parties tab">Information</Trans>
            </Tab>
            <Tab id="contact-library">
              <Trans comment="Label for e-signing parties tab">Contact library</Trans>
            </Tab>
          </TabNav>
        </Box>

        <DialogContent>
          <StyledTabContent tabId="contact-library">
            <Contacts onSelect={() => setTabId('form')}/>
          </StyledTabContent>

          <StyledTabContent tabId="form">
            <Content isEdit={isEdit}/>
          </StyledTabContent>
        </DialogContent>
      </Tabs>

      <Footer setTabId={setTabId} isEdit={isEdit} onClose={onClose}/>
    </FormDialog>);
}
